/**
 * voice-v8 — Tickets-View (Pre.445).
 *
 * Modal das Martin (Superuser) öffnet wenn er die Glocke im Header klickt.
 * Listet offene Tickets, klick auf Ticket öffnet Detail mit Resolve-Button
 * + dev_notes-Textarea.
 */

import { starteAnpassendenTakt } from "./abfrage-takt.js";
import {
  istAufgabe,
  type TicketKind,
} from "../../shared/ticket-arten.js";
import { buildPageHeader } from "./sheet-ui.js";
import { icon, type IconName } from "./icons.js";
import { relativeZeit as relativeTime } from "./relative-time.js";
import { clearPageHash, setPageHash } from "./page-routes.js";
import {
  istOffenMail,
  teileOffene,
  titelFuerAnzeige,
  zwillingsIds,
} from "./tickets-offen.js";
import { istZuhauseTun } from "./zuhause.js";
import { teileAlltagLabor, teileTitel,
  karteZeit,
  zeigeStatusAbzeichen,
} from "./aufgaben-karte.js";

/** Karte sanft entfernen (Hoehe/Deckkraft animiert, dann remove).
    Die Werte sind Animations-Laufzeitwerte — bewusst inline, maxHeight ist
    ein Messwert. */
function animateRowRemoval(row: HTMLElement, ms: number): void {
  row.style.transition =
    "opacity 180ms ease, transform 180ms ease, margin 220ms ease, max-height 220ms ease";
  row.style.overflow = "hidden";
  row.style.maxHeight = `${row.offsetHeight}px`;
  row.style.opacity = "0";
  row.style.transform = "translateX(14px)";
  requestAnimationFrame(() => {
    row.style.maxHeight = "0px";
    row.style.marginTop = "0px";
    row.style.marginBottom = "0px";
  });
  window.setTimeout(() => row.remove(), ms);
}

// 2026-07-08 (Martin): Gmail-Links in der NATIVEN App direkt in der
// Gmail-App oeffnen (wie Kalender & Co.) statt im Browser. googlegmail://
// startet die App; ist sie nicht installiert, faellt der Timeout nach 1,2s
// auf den normalen Browser-Link zurueck. Im Web unveraendert.
function wireNativeGmail(a: HTMLAnchorElement, url: string): void {
  const cap = (
    globalThis as {
      Capacitor?: { isNativePlatform?: () => boolean };
    }
  ).Capacitor;
  if (!cap?.isNativePlatform?.()) return;
  if (!/^https:\/\/mail\.google\.com\//.test(url)) return;
  a.addEventListener("click", (ev) => {
    ev.preventDefault();
    const fallback = window.setTimeout(() => {
      window.open(url, "_blank", "noopener");
    }, 1200);
    document.addEventListener(
      "visibilitychange",
      () => window.clearTimeout(fallback),
      { once: true },
    );
    window.location.href = "googlegmail://";
  });
}

// pre.848: Felder in camelCase — die API (memory/tickets.ts rowToTicket)
// liefert createdAt/userEmail/devNotes/… Vorher las das Frontend snake_case
// → alle diese Felder waren undefined und „undefined" wurde sichtbar
// (relativeTime(undefined) → Literal). Der PATCH-REQUEST bleibt snake_case
// (dev_notes), das erwartet der Server-Endpoint.
interface Ticket {
  id: number;
  userId: number;
  userEmail: string | null;
  title: string;
  description: string;
  severity: "low" | "mid" | "high";
  status: "open" | "in_progress" | "resolved";
  voiceModeAtTime: string | null;
  relatedUrl: string | null;
  /** Ticket 560: angehaengtes Bild (/uploads/...). */
  imageUrl?: string | null;
  // 2026-07-14 (Typecheck): "thread" existiert im Code seit 04.07., fehlte
  // aber im Typ — zwei Vergleiche waren dadurch als unmoeglich markiert.
  kind?: TicketKind;
  devNotes: string | null;
  /** Martins Antworten auf Rueckfragen — eigene Spalte, nie ersetzt. */
  frageAntworten?: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  selfTaskId: string | null;
  pendingQuestion: string | null;
  /** Welche Frage das Feld zeigt (vom Server) — geht mit der Antwort zurück (Ticket 2562). */
  frageKennung?: string | null;
  taskRunning?: boolean;
  proposalReady?: boolean;
  serialQueuedAt?: string | null;
  /**
   * Wie viele gleiche Erinnerungen noch im Stapel liegen („4×“).
   *
   * ⚠️ Der Server liefert das seit dem 06.09.; hier fehlte es im Typ, und
   *    darum konnte diese Liste nach einem Haken nicht sagen, wie viel er
   *    bewirkt hat.
   */
  serieZahl?: number | null;
}

/**
 * Der geteilte Vorlese-Player fuer Meldungen. (09.09.2026)
 *
 * Martin: *„grundsaetzlich lese ich nichts und lasse mir die Dinge
 * vorlesen."*
 *
 * Die Meldungen waren die letzte grosse Textflaeche ohne Stimme — und
 * ausgerechnet sie sind reiner Text: der Tagesbericht, was Vespera nachts
 * bemerkt hat. Wer nicht liest, konnte damit nichts anfangen.
 *
 * ⚠️ Hereingereicht, nicht hier gebaut: `buildNarrationPlayer` lebt in
 *    app.ts, und nur der eine Player weiss von der Abspielleiste ueber der
 *    Fusszeile. Dasselbe Muster wie beim Fund, bei Projekten und im Spiegel;
 *    ein eigener waere der sechste, der dasselbe tut.
 */
type MeldungsSpieler = (opts: {
  endpoint: string;
  getBody: (voiceId: string) => Record<string, unknown> | null;
  titel?: string;
}) => { el: HTMLElement; setReady?: (ok: boolean) => void };

let meldungsSpieler: MeldungsSpieler | null = null;

/** Einmal beim Start aus app.ts setzen. */
export function setzeMeldungsSpieler(bauer: MeldungsSpieler): void {
  meldungsSpieler = bauer;
}

/**
 * Ab wann sich das Vorlesen lohnt.
 *
 * ⚠️ Unter dieser Laenge ist Hinsehen schneller als Zuhoeren — eine Leiste
 *    an jeder Zweizeilen-Erinnerung („5 bis 10 Klimmzuege") waere Zierat und
 *    wuerde die Liste zumuellen.
 */
export const VORLESE_MINDEST_ZEICHEN = 180;

interface ApiResponse {
  tickets: Ticket[];
}

let openModalRef: HTMLElement | null = null;
/*
 * ⚠️ 09.09.2026 — hier stand `badgePollingTimer`, ein Rest aus der Zeit des
 *    starren 15-Sekunden-Takts. Seit der Takt ueber `starteAnpassendenTakt`
 *    laeuft, wurde die Variable NIE MEHR GESETZT — und damit war
 *    `if (badgePollingTimer) return;` immer falsch.
 *
 *    Folge: Ein zweiter Aufruf haette die drei Ereignis-Zuhoerer und den
 *    Takt ein zweites Mal angemeldet, also doppelt abgefragt. Genau die
 *    Sorte stiller Verdopplung, gegen die der Wachter gedacht war — und
 *    genau das, was Martin am 31.07. als leeren Akku gemeldet hat.
 */
let badgeTaktLaeuft = false;


function severityClass(s: string): string {
  if (s === "high") return "tickets-badge-high";
  if (s === "low") return "tickets-badge-low";
  return "tickets-badge-mid";
}

async function fetchTickets(
  filter: "open" | "all" | "resolved",
): Promise<Ticket[]> {
  const url =
    filter === "all"
      ? "/api/tickets?status=all"
      : filter === "resolved"
        ? "/api/tickets?status=resolved"
        : "/api/tickets?status=open";
  try {
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) return [];
    const j = (await r.json()) as ApiResponse;
    let tickets = Array.isArray(j.tickets) ? j.tickets : [];
    if (filter === "open") {
      // open + in_progress
      const r2 = await fetch("/api/tickets?status=in_progress", {
        credentials: "include",
      });
      if (r2.ok) {
        const j2 = (await r2.json()) as ApiResponse;
        tickets = [
          ...tickets,
          ...(Array.isArray(j2.tickets) ? j2.tickets : []),
        ];
      }
    }
    return tickets;
  } catch {
    return [];
  }
}

async function updateTicket(
  id: number,
  patch: { status?: string; dev_notes?: string | null; serie_ganz?: boolean },
): Promise<Ticket | null> {
  try {
    const r = await fetch(`/api/tickets/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { ticket?: Ticket };
    return j.ticket ?? null;
  } catch {
    return null;
  }
}

/** Letzter offener Stand — Zwillinge derselben Sache mit-erledigen. */
let letzteOffeneTickets: Ticket[] = [];

/** Eine Karte erledigen heisst die Sache erledigen, nicht nur die erste Zeile. */
/**
 * @param ganz Den ganzen Stapel schliessen. Nur „Alle erledigen“ tut das.
 *
 * ⚠️⚠️ 10.09.2026 — EIN HAKEN ZAEHLT HERUNTER. DREI SAETZE, ZWEI DAFUER.
 *
 * Hier stand seit dem 09.09. das Gegenteil: ein Haken schloss bei einer
 * Erinnerung den GANZEN Stapel. Grundlage war Martins Satz *„Wenn ich auf das
 * Haekchen druecke, um es zu bestaetigen, dann verschwindet es nicht.“*
 *
 * Der Satz war eine Beschwerde ueber die RUECKMELDUNG, nicht ueber die Zahl:
 * Der Haken kam an und zaehlte den Stapel herunter — die Zeile blieb stehen
 * und sagte kein Wort dazu. Beantwortet wurde er in derselben Runde durch die
 * Quittung („Abgehakt — noch 2 offen“). Der ganze Stapel war eine zweite,
 * unnoetige Antwort auf dieselbe Frage — und sie kostet Daten.
 *
 * Die beiden anderen Saetze sagen dasselbe, und der zweite kam mit Zahl:
 *   06.09.: *„wenn Klimmzuege gestapelt werden und ich druecke einmal auf
 *   bestaetigen, dann sind alle weg. Das ist nicht gut.“*
 *   10.09.: *„Ein grosser Fehler ist ... schon vier oder fuenf im Topf ...
 *   ich einmal draufklicke: Der ganze Topf ist weg.“*
 *
 * Nachgemessen, genau zu diesem Satz: Ticket 2204 („5 bis 10 Klimmzuege“,
 * serie_zahl 4) ging um 19:57:34 Ortszeit in EINEM Schritt auf `resolved`,
 * Ticket 2199 („Raus mit dir“, serie_zahl 2) um 19:49:31 ebenso.
 */
/**
 * Ist nach dem Haken noch etwas offen? Bei einem Stapel („3×") zaehlt ein
 * Haken nur herunter — die Zeile bleibt dann stehen und zeigt „2×".
 */
function nochOffen(t: Ticket | null): boolean {
  return !!t && (t.status === "open" || t.status === "in_progress");
}

/**
 * Sagen, dass der Haken gezaehlt hat. (10.09.2026)
 *
 * ⚠️ Auf Zuhause steht die Zahl an der Zeile („Klimmzuege · 4×“), hier nicht.
 *    Ein Haken, der den Stapel von 4 auf 3 zaehlt, sieht in dieser Liste
 *    deshalb aus wie ein Tipp ins Leere — genau daran ist Martin am 09.09.
 *    haengengeblieben. Derselbe Wortlaut wie die Quittung auf Zuhause.
 */
function meldeStapelStand(t: Ticket): void {
  const rest = Math.max(0, Number(t.serieZahl ?? 0));
  if (rest < 1) return;
  window.dispatchEvent(
    new CustomEvent("voice-v8:toast", {
      detail: {
        message:
          rest > 1
            ? `Abgehakt — noch ${rest} offen`
            : "Abgehakt — noch eines offen",
        /*
         * 14.09.2026 (Martin: „ich sehe immer noch keinen gruenen Punkt als
         * Bestaetigung"): Eine Bestaetigung gehoert in die Farbe, in der
         * diese App „erledigt" sagt. `info` sah aus wie jeder andere Hinweis.
         */
        kind: "success",
      },
    }),
  );
}

async function erledigeTicket(id: number, ganz = false): Promise<Ticket | null> {
  const ids = zwillingsIds(id, letzteOffeneTickets);
  let anker: Ticket | null = null;
  for (const i of ids) {
    const t = await updateTicket(i, { status: "resolved", ...(ganz ? { serie_ganz: true } : {}) });
    if (i === id) anker = t;
  }
  return anker;
}


/** Pre.787 (#55): absolutes Datum "11.06.2026, 07:42" (de-AT, lokale Zeit). */
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// 2026-07-07 (Martin, Glocke-Politur): Markdown-Reste (**fett**, __x__,
// #-Header) aus Job-Texten nicht roh anzeigen.
// 2026-07-08 (Martin): Deko-Emojis aus Meldungstexten entfernen — nur ruhige
// Icons statt bunter Symbole. Pfeile (->, VIE-FAO) bleiben absichtlich.
function stripEmoji(s: string): string {
  /*
   * ⚠️ 09.09.2026 — der Variationswaehler (FE0F) und der Nullbreiten-
   *    Verbinder (200D) standen MIT IN der Zeichenklasse. Beide sind aber
   *    keine eigenen Zeichen, sondern verbinden sich mit dem davor; in einer
   *    Klasse gelesen koennen sie zusammengesetzte Emojis halb stehen lassen.
   *    Deshalb erst die Bilder, dann die Verbinder.
   */
  return s
    .replace(
      /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{2300}-\u{23FF}]/gu,
      "",
    )
    .replace(/\uFE0F|\u200D/gu, "");
}

import { buildMeldungCard } from "./meldung-card.js";

function stripMdResidue(s: string): string {
  return s
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/^\s*>\s?/gm, "")
    .replace(/^#{1,4}\s+/gm, "")
    .replace(/\*/g, "");
}

function renderTicketRow(ticket: Ticket, onUpdate: () => void): HTMLElement {
  const row = document.createElement("div");
  row.className = "tickets-row";
  row.dataset.ticketStatus = ticket.status;

  const head = document.createElement("div");
  head.className = "tickets-row-head";

  const id = document.createElement("span");
  id.className = "tickets-row-id";
  id.textContent = `#${ticket.id}`;
  head.appendChild(id);

  const sev = document.createElement("span");
  sev.className = `tickets-badge ${severityClass(ticket.severity)}`;
  sev.textContent = ticket.severity;
  head.appendChild(sev);

  // 2026-08-26: Das Status-Abzeichen erscheint nur noch, wenn es vom
  // Erwartbaren abweicht — siehe zeigeStatusAbzeichen(). Auf dem Reiter
  // "Aufgaben" ist alles offen; "OFFEN" in jeder Zeile sagte nichts.
  if (zeigeStatusAbzeichen(ticket.status)) {
    const status = document.createElement("span");
    status.className = "tickets-badge tickets-badge-status";
    status.dataset.status = ticket.status;
    status.textContent =
      ticket.status === "in_progress" ? "in Arbeit" : "erledigt";
    head.appendChild(status);
  }

  const ago = document.createElement("span");
  ago.className = "tickets-row-time";
  // Pre.787 (#55): absolutes Anlegedatum zusätzlich zur Relativzeit; bei
  // erledigten Tickets auch das Erledigt-Datum.
  // 2026-08-26: beides stand NEBENEINANDER in der Liste ("vor 6h ·
  // 26.08.2026, 04:01"). Beim Ueberfliegen zaehlt die Relativzeit; das Datum
  // bleibt erreichbar, aber am title statt in der Zeile.
  const zeit = karteZeit(
    relativeTime(ticket.createdAt),
    fmtDateTime(ticket.createdAt),
    ticket.status === "resolved" ? fmtDateTime(ticket.resolvedAt) : "",
  );
  ago.textContent = zeit.sichtbar;
  if (zeit.beiVerweilen) ago.title = zeit.beiVerweilen;
  head.appendChild(ago);

  row.appendChild(head);

  const title = document.createElement("div");
  title.className = "tickets-row-title";
  const titelTeile = teileTitel(ticket.title);
  title.appendChild(document.createTextNode(titelTeile.alltag));
  if (titelTeile.labor) {
    const laborTitel = document.createElement("span");
    laborTitel.className = "tickets-row-labor";
    laborTitel.textContent = titelTeile.labor;
    title.appendChild(document.createTextNode(" "));
    title.appendChild(laborTitel);
  }
  // 2026-07-26 (Martin: "die Aufgaben auch mitklappen, sodass man die grobe
  // Ueberschrift sieht, und wenn man Details haben moechte, kann man sie
  // anzeigen"): Der Titel ist der Schalter — dieselbe Geste wie bei den
  // Meldungen. Eingeklappt bleiben Kennung, Schweregrad, Status, Zeit und
  // Titel stehen; alles Weitere kommt auf Tipp.
  title.classList.add("is-toggle");
  title.setAttribute("role", "button");
  title.tabIndex = 0;
  title.setAttribute("aria-expanded", "false");
  const klappen = (): void => {
    const offen = row.classList.toggle("is-expanded");
    title.setAttribute("aria-expanded", offen ? "true" : "false");
  };
  title.addEventListener("click", klappen);
  title.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      klappen();
    }
  });
  row.appendChild(title);

  const meta = document.createElement("div");
  meta.className = "tickets-row-meta";
  // Pre.787 (#55): nie "undefined" rendern — Feldnamen defensiv (API
  // lieferte je nach Pfad user_id ODER userId) + Literal-"undefined" abfangen.
  const mail =
    typeof ticket.userEmail === "string" &&
    ticket.userEmail &&
    ticket.userEmail !== "undefined"
      ? ticket.userEmail
      : null;
  meta.textContent = mail
    ? `User: ${mail}`
    : ticket.userId != null
      ? `User #${ticket.userId}`
      : "User —";
  row.appendChild(meta);

  const desc = document.createElement("div");
  desc.className = "tickets-row-desc";
  // Fix Flugwaechter-Darstellung: automatische Meldungen (Titel "Geplant: …")
  // als aufklappbare Karte — gleiches Erscheinungsbild wie im Chat.
  const isAutoMeldung =
    ticket.kind === "notification" && /^Geplant:/.test(ticket.title);
  if (isAutoMeldung) {
    const cardTitle = ticket.title.replace(/^Geplant:\s*/, "");
    const bodyMd = ticket.description
      .replace(/\s*\(Automatischer geplanter Auftrag von Vespera\.\)\s*$/, "")
      .trim();
    desc.appendChild(buildMeldungCard(cardTitle, bodyMd));
  } else {
    // 2026-07-04 (Martin): URLs klickbar machen (z.B. Musik-Link einer geplanten
    // Meldung) — vorher reiner Text, nicht antippbar.
    const beschreibung = teileAlltagLabor(stripMdResidue(ticket.description));
    for (const part of beschreibung.alltag.split(
      /(https?:\/\/[^\s)>\]]+)/g,
    )) {
      if (/^https?:\/\//.test(part)) {
        const a = document.createElement("a");
        a.href = part;
        a.textContent = part;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.className = "tickets-row-link";
        desc.appendChild(a);
      } else if (part) {
        desc.appendChild(document.createTextNode(part));
      }
    }
    /*
     * ⚠️ Die Leiste steht NACH dem Text, nicht davor — anders als beim Fund.
     *    Dort ist der Text der ganze Inhalt der Seite; hier ist die Meldung
     *    eine Karte in einer Liste, und eine Abspielleiste ueber jedem
     *    Absatz waere ein Zaun quer durch die Liste. Wer nicht liest, hat sie
     *    trotzdem in Reichweite: Sie sitzt am Ende der aufgeklappten Karte,
     *    genau dort, wo die Knoepfe stehen.
     */
    if (
      meldungsSpieler &&
      ticket.kind === "notification" &&
      (ticket.description ?? "").trim().length >= VORLESE_MINDEST_ZEICHEN
    ) {
      const spieler = meldungsSpieler({
        endpoint: `/api/tickets/${ticket.id}/narrate`,
        titel: ticket.title,
        getBody: (voiceId) => ({ voiceId }),
      });
      const reihe = document.createElement("div");
      reihe.className = "tickets-vorlesen";
      reihe.appendChild(spieler.el);
      desc.appendChild(reihe);
      spieler.setReady?.(true);
    }
    if (beschreibung.labor) {
      const laborDesc = document.createElement("div");
      laborDesc.className = "tickets-row-labor";
      laborDesc.textContent = beschreibung.labor;
      desc.appendChild(laborDesc);
    }
  }
  row.appendChild(desc);

  // Prominenter Öffnen-Button, wenn die Meldung einen Link mitbringt.
  if (ticket.relatedUrl && /^https?:\/\//.test(ticket.relatedUrl)) {
    const open = document.createElement("a");
    open.href = ticket.relatedUrl;
    open.target = "_blank";
    open.rel = "noopener noreferrer";
    open.className = "tickets-row-open";
    wireNativeGmail(open, ticket.relatedUrl);
    open.textContent = "▶︎ Öffnen";
    row.appendChild(open);
  }

  // Ticket 560 (2026-07-25, Martin): angehaengtes Bild als Vorschau. Tippen
  // oeffnet es in voller Groesse — so kommt visuelle Information bei der
  // Entwicklung an, statt nur beschrieben zu werden.
  if (ticket.imageUrl?.startsWith("/uploads/")) {
    const bildBtn = document.createElement("button");
    bildBtn.type = "button";
    bildBtn.className = "tickets-row-image";
    bildBtn.title = "Bild in voller Größe öffnen";
    const im = document.createElement("img");
    im.src = `/thumb?p=${encodeURIComponent(ticket.imageUrl)}&w=320`;
    im.alt = "Angehängtes Bild";
    im.loading = "lazy";
    bildBtn.appendChild(im);
    bildBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      window.open(ticket.imageUrl as string, "_blank", "noopener");
    });
    row.appendChild(bildBtn);
  }
  if (ticket.devNotes) {
    const notes = document.createElement("div");
    notes.className = "tickets-row-notes";
    notes.textContent = `Notizen: ${ticket.devNotes}`;
    row.appendChild(notes);
  }
  // Martins eigene Antworten stehen getrennt von den Notizen — sie duerfen
  // nicht mit ihnen zusammen ueberschrieben werden (11.09.2026).
  if (ticket.frageAntworten) {
    const ant = document.createElement("div");
    ant.className = "tickets-row-notes";
    ant.textContent = `Deine Antwort: ${ticket.frageAntworten}`;
    row.appendChild(ant);
  }

  // pre.850: Offene RÜCKFRAGE der KI — Frage + Antwortfeld direkt im Ticket.
  // Die Antwort setzt die Selbstverbesserungs-Session per Resume fort.
  if (ticket.pendingQuestion) {
    row.dataset.waiting = "1";
    const qbox = document.createElement("div");
    qbox.className = "tickets-row-question";

    const qlabel = document.createElement("div");
    qlabel.className = "tickets-question-label";
    qlabel.textContent = "Die KI fragt nach:";
    qbox.appendChild(qlabel);

    const qtext = document.createElement("div");
    qtext.className = "tickets-question-text";
    qtext.textContent = ticket.pendingQuestion;
    qbox.appendChild(qtext);

    const answer = document.createElement("textarea");
    answer.className = "tickets-answer-input";
    answer.rows = 2;
    answer.placeholder = "Deine Antwort an die KI …";
    qbox.appendChild(answer);

    const sendBtn = document.createElement("button");
    sendBtn.type = "button";
    sendBtn.className = "tickets-btn tickets-btn--primary";
    sendBtn.textContent = "Antwort senden";
    sendBtn.addEventListener("click", async () => {
      const text = answer.value.trim();
      if (!text) {
        answer.focus();
        return;
      }
      sendBtn.disabled = true;
      sendBtn.textContent = "Sende …";
      try {
        const r = await fetch(`/api/tickets/${ticket.id}/answer`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          // Die Frage, die hier steht — der Server legt nur zu GENAU ihr ab (Ticket 2562).
          body: JSON.stringify({ frage: ticket.frageKennung, answer: text }),
        });
        const j = (await r.json().catch(() => null)) as {
          ok?: boolean;
          error?: string;
          weiter?: boolean;
        } | null;
        if (r.ok && j?.ok) {
          /*
           * ⚠️ 11.09.2026: Hier stand IMMER „die KI macht jetzt weiter, du
           *    bekommst eine Nachricht, wenn sie fertig ist" — auch dann,
           *    wenn gar keine Sitzung laeuft und nichts anlaeuft. Martin
           *    haette auf eine Nachricht gewartet, die nie kommt. Der Server
           *    sagt jetzt mit `weiter`, was wirklich passiert.
           */
          window.dispatchEvent(
            new CustomEvent("voice-v8:toast", {
              detail: {
                message:
                  j.weiter === false
                    ? "Antwort gespeichert. Sie steht am Ticket — die Arbeit daran laeuft gerade nicht, es kommt also keine Rueckmeldung."
                    : "Antwort gesendet — die KI macht jetzt weiter. Du bekommst eine Nachricht, wenn sie fertig ist.",
                kind: "success",
              },
            }),
          );
          onUpdate();
        } else {
          sendBtn.disabled = false;
          sendBtn.textContent = "Antwort senden";
          window.dispatchEvent(
            new CustomEvent("voice-v8:toast", {
              detail: {
                /*
                 * 409 (Ticket 2562): hier stand eine andere Frage, als jetzt
                 * wartet — nichts ist gespeichert. Sein Text bleibt im Feld.
                 */
                message:
                  r.status === 409
                    ? "Nichts gespeichert: Die Frage hat sich inzwischen geändert. Lade die Ansicht neu und lies sie, bevor du antwortest – dein Text steht noch im Feld."
                    : `Antwort konnte nicht gesendet werden: ${j?.error ?? r.status}`,
                kind: "error",
              },
            }),
          );
        }
      } catch {
        sendBtn.disabled = false;
        sendBtn.textContent = "Antwort senden";
      }
    });
    qbox.appendChild(sendBtn);
    row.appendChild(qbox);
  }

  // Actions
  const actions = document.createElement("div");
  actions.className = "tickets-row-actions";

  // 2026-07-04 (Martin): reine Meldungen (Erinnerungen/Infos) brauchen KEINE
  // Dev-Ticket-Knoepfe (In Arbeit / Von KI beheben / Serien-Lauf) — nur einen
  // schlichten "Gelesen"-Knopf zum Wegraeumen.
  // 14.09.2026: auch `system` — eine Technik-Diagnose verlangt von Martin
  // nichts und braucht darum keine Entwickler-Knoepfe. Siehe shared/ticket-arten.
  if (!istAufgabe(ticket.kind)) {
    if (ticket.status !== "resolved") {
      const seen = document.createElement("button");
      seen.type = "button";
      seen.className = "tickets-btn tickets-btn--ghost";
      seen.textContent = ticket.kind === "thread" ? "Erledigt" : "Gelesen";
      seen.addEventListener("click", async () => {
        seen.disabled = true;
        const updated = await erledigeTicket(ticket.id);
        if (updated && nochOffen(updated)) {
          // Stapel: einer ist erledigt, der Rest bleibt stehen — und sagt es.
          seen.disabled = false;
          meldeStapelStand(updated);
          window.dispatchEvent(new CustomEvent("voice-v8:tickets-changed"));
        } else if (updated) {
          // 2026-07-05 (Martin): NUR diese Karte entfernen (kein Voll-Reload,
          // kein Zuckeln) — Badge sofort per Event aktualisieren.
          animateRowRemoval(row, 230);
          window.dispatchEvent(new CustomEvent("voice-v8:tickets-changed"));
        } else {
          seen.disabled = false;
          window.dispatchEvent(
            new CustomEvent("voice-v8:toast", {
              detail: {
                message: "Konnte nicht als gelesen markiert werden — nochmal versuchen.",
                kind: "error",
              },
            }),
          );
        }
      });
      actions.appendChild(seen);
    }
    row.appendChild(actions);
    return row;
  }

  // pre.864 (Martins Wunsch): liegt ein fertiger KI-Vorschlag bereit, kann er
  // hier mobil LIVE geschaltet werden (Build-Gate + Auto-Rollback serverseitig).
  if (ticket.proposalReady && !ticket.taskRunning) {
    const goLive = document.createElement("button");
    goLive.type = "button";
    goLive.className = "tickets-btn tickets-btn--golive";
    goLive.textContent = "Freischalten & live setzen";
    goLive.addEventListener("click", async () => {
      const { confirmSheet } = await import("./sheet-ui.js");
      const okGo = await confirmSheet({
        title: `Ticket #${ticket.id} live schalten?`,
        message:
          "Der KI-Vorschlag wird geprüft (Build-Gate) und live geschaltet. Kommt der Server nicht sauber hoch, wird automatisch zurückgerollt. Du bekommst eine Meldung.",
        confirmLabel: "Live schalten",
      });
      if (!okGo) return;
      goLive.disabled = true;
      goLive.textContent = "Schalte live …";
      try {
        const r = await fetch(`/api/tickets/${ticket.id}/apply`, {
          method: "POST",
          credentials: "include",
        });
        const j = (await r.json().catch(() => null)) as {
          ok?: boolean;
          error?: string;
        } | null;
        window.dispatchEvent(
          new CustomEvent("voice-v8:toast", {
            detail:
              r.ok && j?.ok
                ? {
                    message:
                      "Freischalten läuft (Build-Gate + Auto-Rollback) — du bekommst gleich eine Meldung. Der Server startet dabei kurz neu.",
                    kind: "info",
                    ms: 7000,
                  }
                : {
                    message: `Freischalten nicht möglich: ${j?.error ?? r.status}`,
                    kind: "error",
                  },
          }),
        );
        if (!(r.ok && j?.ok)) {
          goLive.disabled = false;
          goLive.textContent = "Freischalten & live setzen";
        }
      } catch {
        goLive.disabled = false;
        goLive.textContent = "Freischalten & live setzen";
      }
    });
    actions.appendChild(goLive);
  }

  if (ticket.status !== "resolved") {
    if (ticket.status === "open") {
      const inProgressBtn = document.createElement("button");
      inProgressBtn.type = "button";
      inProgressBtn.className = "tickets-btn tickets-btn--ghost";
      inProgressBtn.textContent = "In Arbeit setzen";
      inProgressBtn.addEventListener("click", async () => {
        const updated = await updateTicket(ticket.id, {
          status: "in_progress",
        });
        if (updated) onUpdate();
      });
      actions.appendChild(inProgressBtn);
    }
    const resolveBtn = document.createElement("button");
    resolveBtn.type = "button";
    resolveBtn.className = "tickets-btn tickets-btn--primary";
    resolveBtn.textContent = "Auf erledigt setzen";
    resolveBtn.addEventListener("click", async () => {
      const updated = await erledigeTicket(ticket.id);
      if (updated) onUpdate();
    });
    actions.appendChild(resolveBtn);
  } else {
    const reopenBtn = document.createElement("button");
    reopenBtn.type = "button";
    reopenBtn.className = "tickets-btn tickets-btn--ghost";
    reopenBtn.textContent = "Wieder öffnen";
    reopenBtn.addEventListener("click", async () => {
      const updated = await updateTicket(ticket.id, { status: "open" });
      if (updated) onUpdate();
    });
    actions.appendChild(reopenBtn);
  }

  // pre.848/855 (Martins Wunsch): Bugmeldung von der KI beheben lassen.
  // Läuft bereits ein Task für DIESES Ticket → „läuft …" + Abbrechen statt
  // eines erneut klickbaren Start-Buttons (verhindert Doppelstart).
  if (ticket.taskRunning) {
    const runn = document.createElement("span");
    runn.className = "tickets-task-running";
    runn.textContent = "● KI arbeitet daran …";
    actions.appendChild(runn);
    if (ticket.selfTaskId) {
      const abort = document.createElement("button");
      abort.type = "button";
      abort.className = "tickets-btn tickets-btn--ghost";
      abort.textContent = "Abbrechen";
      abort.addEventListener("click", async () => {
        abort.disabled = true;
        abort.textContent = "Breche ab …";
        try {
          const r = await fetch(
            `/api/background-tasks/${encodeURIComponent(ticket.selfTaskId!)}/cancel`,
            { method: "POST", credentials: "include" },
          );
          if (r.ok) {
            window.dispatchEvent(
              new CustomEvent("voice-v8:toast", {
                detail: { message: "KI-Behebung abgebrochen.", kind: "info" },
              }),
            );
            onUpdate();
          } else {
            abort.disabled = false;
            abort.textContent = "Abbrechen";
          }
        } catch {
          abort.disabled = false;
          abort.textContent = "Abbrechen";
        }
      });
      actions.appendChild(abort);
    }
  } else if (ticket.status !== "resolved") {
    const fixBtn = document.createElement("button");
    fixBtn.type = "button";
    fixBtn.className = "tickets-btn tickets-btn--primary";
    fixBtn.textContent = "Von KI beheben lassen";
    fixBtn.addEventListener("click", async () => {
      fixBtn.disabled = true;
      fixBtn.textContent = "Starte CLI …";
      try {
        const r = await fetch(`/api/tickets/${ticket.id}/fix`, {
          method: "POST",
          credentials: "include",
        });
        const j = (await r.json().catch(() => null)) as {
          ok?: boolean;
          error?: string;
        } | null;
        if (r.ok && j?.ok) {
          window.dispatchEvent(
            new CustomEvent("voice-v8:toast", {
              detail: {
                message:
                  "KI-Behebung gestartet — du bekommst eine Nachricht, sobald der Vorschlag fertig ist.",
                kind: "success",
              },
            }),
          );
          onUpdate();
        } else {
          // bleibt deaktiviert bei „läuft bereits"; sonst wieder freigeben.
          const already = /bereits eine Selbstverbesserung/i.test(
            j?.error ?? "",
          );
          fixBtn.textContent = already
            ? "Läuft bereits …"
            : "Von KI beheben lassen";
          if (!already) fixBtn.disabled = false;
          window.dispatchEvent(
            new CustomEvent("voice-v8:toast", {
              detail: {
                message: already
                  ? "Es läuft bereits eine KI-Behebung — bitte warten oder abbrechen."
                  : `Konnte CLI nicht starten: ${j?.error ?? r.status}`,
                kind: already ? "warning" : "error",
              },
            }),
          );
          if (already) onUpdate();
        }
      } catch {
        fixBtn.disabled = false;
        fixBtn.textContent = "Von KI beheben lassen";
      }
    });
    actions.appendChild(fixBtn);
  }

  const noteBtn = document.createElement("button");
  noteBtn.type = "button";
  noteBtn.className = "tickets-btn tickets-btn--ghost";
  noteBtn.textContent = "+ Notiz";
  noteBtn.addEventListener("click", async () => {
    // Pre.821: App-Dialog statt Browser-prompt (Standard seit pre.796).
    const { promptSheet } = await import("./sheet-ui.js");
    const note = await promptSheet({
      title: `Notiz zu Ticket #${ticket.id}`,
      confirmLabel: "Speichern",
    });
    if (!note) return;
    const updated = await updateTicket(ticket.id, { dev_notes: note });
    if (updated) onUpdate();
  });
  actions.appendChild(noteBtn);

  // pre.871 (#145): Toggle „fuer den Serien-Lauf freigeben".
  if (ticket.status !== "resolved") {
    const queued = ticket.serialQueuedAt != null;
    const serieBtn = document.createElement("button");
    serieBtn.type = "button";
    serieBtn.className =
      "tickets-btn tickets-btn--ghost" + (queued ? " is-queued" : "");
    serieBtn.textContent = queued
      ? "\u2713 Im Serien-Lauf"
      : "Für Serien-Lauf freigeben";
    serieBtn.addEventListener("click", async () => {
      serieBtn.disabled = true;
      try {
        const r = await fetch(`/api/tickets/${ticket.id}/queue`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ queued: !queued }),
        });
        if (r.ok) onUpdate();
        else serieBtn.disabled = false;
      } catch {
        serieBtn.disabled = false;
      }
    });
    actions.appendChild(serieBtn);
  }

  row.appendChild(actions);

  return row;
}

// 2026-07-05 (Martin): SCHLICHTE Meldungs-Zeile — auf einen Blick lesbar.
// Titel + Zeit, kurzer Text (Links klickbar), hoechstens "Oeffnen" + "Gelesen".
// KEINE Badges/IDs/Status/Dev-Knoepfe (die gehoeren zu den Aufgaben).
// 2026-07-05 (Martin): eigenes Icon je Meldungs-Typ (statt Einheits-Punkt).
function notificationIcon(t: Ticket): IconName {
  if (t.kind === "thread") return "clock";
  if (istOffenMail(t)) return "mail";
  const s = `${t.title} ${t.description}`.toLowerCase();
  if (/flug|flight|preis|ticket/.test(s)) return "plane";
  if (/musik|lied|song|van veen/.test(s)) return "music";
  if (/termin|kalender|meeting/.test(s)) return "calendar";
  if (/push-?up|liegest|trink|wasser|sport|bewegung|klimmzug/.test(s))
    return "activity";
  if (/erinnerung|\u23f0/.test(s)) return "clock";
  return "bell";
}

function renderNotificationRow(ticket: Ticket, onUpdate: () => void): HTMLElement {
  const row = document.createElement("div");
  row.className = "note-row";
  row.dataset.ticketStatus = ticket.status;

  const iconEl = document.createElement("div");
  iconEl.className = "note-row-icon";
  iconEl.appendChild(icon(notificationIcon(ticket), { size: 16 }));
  row.appendChild(iconEl);

  const main = document.createElement("div");
  main.className = "note-row-main";

  // 2026-07-24 (Martin: "viel zu viel Text, das gehoert eingeklappt"): Karte
  // standardmaessig EINGEKLAPPT — nur Titel + Zeit + Aktionen. Klick auf die
  // Kopfzeile klappt den vollen Text auf.
  const bodyText = stripMdResidue(ticket.description || "")
    .replace(/\n*\(Automatischer geplanter Auftrag[^)]*\)\s*$/i, "")
    .replace(/^\u2713\s*/, "")
    .trim();

  const top = document.createElement("div");
  top.className = "note-row-top";
  const title = document.createElement("div");
  title.className = "note-row-title";
  title.textContent =
    stripEmoji(titelFuerAnzeige(ticket.title))
      .replace(/\s{2,}/g, " ")
      .trim() || ticket.title;
  top.appendChild(title);
  /*
   * ⚠️ 14.09.2026 — DIE ZAHL FEHLTE GENAU DORT, WO ER SIE BRAUCHT.
   *
   * Der Kommentar bei `meldeStapelStand` sagt es seit dem 10.09.: „Auf
   * Zuhause steht die Zahl an der Zeile, hier nicht." Ein Haken, der den
   * Stapel von 4 auf 3 zaehlt, veraendert in dieser Liste sonst nichts
   * Sichtbares — und sieht aus wie ein Tipp ins Leere.
   *
   * Nachgemessen am 14.09.: „Raus mit dir" stand bei 9, „Klimmzuege" bei 14.
   * Wer das nicht sieht, haelt den Knopf fuer kaputt.
   */
  const stapel = document.createElement("span");
  stapel.className = "note-row-stapel";
  const zeigeStapel = (n: number): void => {
    stapel.textContent = `${n}\u00d7`;
    stapel.hidden = n < 2;
  };
  zeigeStapel(Number(ticket.serieZahl ?? 0));
  top.appendChild(stapel);
  const time = document.createElement("span");
  time.className = "note-row-time";
  time.textContent = relativeTime(ticket.createdAt);
  top.appendChild(time);
  if (bodyText) {
    const caret = document.createElement("span");
    caret.className = "note-row-caret";
    caret.setAttribute("aria-hidden", "true");
    top.appendChild(caret);
    top.classList.add("is-toggle");
    row.classList.add("has-body");
    top.setAttribute("role", "button");
    top.tabIndex = 0;
    const toggle = (): void => {
      row.classList.toggle("is-expanded");
    };
    top.addEventListener("click", toggle);
    top.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        toggle();
      }
    });
  }
  main.appendChild(top);
  if (bodyText) {
    const desc = document.createElement("div");
    desc.className = "note-row-body";
    for (const rawLine of bodyText.split("\n")) {
      if (/^\s*-{3,}\s*$/.test(rawLine)) {
        const hr = document.createElement("hr");
        hr.className = "note-hr";
        desc.appendChild(hr);
        continue;
      }
      const line = stripEmoji(rawLine).replace(/\s{2,}/g, " ").trim();
      if (!line) continue;
      const p = document.createElement("div");
      p.className = "note-line";
      for (const part of line.split(/(https?:\/\/[^\s)>\]]+)/g)) {
        if (/^https?:\/\//.test(part)) {
          const a2 = document.createElement("a");
          a2.href = part;
          a2.target = "_blank";
          a2.rel = "noopener";
          a2.className = "note-row-inlinelink";
          a2.textContent = "Link";
          p.appendChild(a2);
        } else if (part) {
          p.appendChild(document.createTextNode(part));
        }
      }
      desc.appendChild(p);
    }
    main.appendChild(desc);
  }

  const actions = document.createElement("div");
  actions.className = "note-row-actions";
  // 2026-07-24 (Martin): "Antwort entwerfen" — laedt die vorgeschlagene
  // Aufforderung ("entwirf eine Antwort an X") direkt in den Chat, statt
  // dass Martin sie abtippen muss. Meldungen schliessen, Chat oeffnen.
  const draftPrompt = ((): string => {
    const d = String(ticket.description || "");
    const m =
      d.match(
        /entwirf eine Antwort an\s+([^\n\u201C\u201D\u201E"\u00AB\u00BB]+?)\s*[\u201C\u201D\u201E"\u00AB\u00BB]/i,
      ) || d.match(/entwirf eine Antwort an\s+([^\n.]+)/i);
    if (m && m[1])
      return `entwirf eine Antwort an ${m[1]
        .trim()
        .replace(/["\u201C\u201D\u201E,.\s]+$/u, "")}`;
    const tm = String(ticket.title || "").match(
      /E-Mails?\s+von\s+(.+?)\s+brauch/i,
    );
    if (tm && tm[1]) return `entwirf eine Antwort an ${tm[1].trim()}`;
    return "";
  })();
  if (draftPrompt) {
    const draft = document.createElement("button");
    draft.type = "button";
    draft.className = "note-btn note-btn--draft";
    draft.textContent = "Antwort entwerfen";
    draft.addEventListener("click", (ev) => {
      ev.stopPropagation();
      closeTicketsModal();
      window.dispatchEvent(
        new CustomEvent("voice-v8:example-prompt", {
          detail: { prompt: draftPrompt },
        }),
      );
    });
    actions.appendChild(draft);
  }
  if (ticket.relatedUrl) {
    const open = document.createElement("a");
    open.className = "note-btn note-btn--open";
    open.href = ticket.relatedUrl;
    open.target = "_blank";
    open.rel = "noopener";
    wireNativeGmail(open, ticket.relatedUrl);
    open.appendChild(icon("external", { size: 15 }));
    const openLbl = document.createElement("span");
    openLbl.textContent = "Öffnen";
    open.appendChild(openLbl);
    actions.appendChild(open);
  }
  if (ticket.status !== "resolved") {
    const seen = document.createElement("button");
    seen.type = "button";
    // 2026-07-25 (Martin: "das wirkt nicht so notification-maessig"): runder
    // Haken in der Titelzeile statt bildschirmbreitem Knopf darunter. Die
    // Beruehrflaeche bleibt gross genug; die Bedeutung steht in Beschriftung
    // und Tooltip, damit sie auch ohne Blick aufs Symbol klar ist.
    seen.className = "note-check";
    // 2026-07-08 (Martin, 3x moniert): Erinnerungen werden ERLEDIGT, nicht
    // gelesen — nur reine Info-Meldungen behalten "Gelesen".
    const istErinnerung = /\u23f0|erinner/i.test(ticket.title);
    const seenText =
      ticket.kind === "thread" || istErinnerung ? "Erledigt" : "Gelesen";
    seen.setAttribute("aria-label", seenText);
    seen.title = seenText;
    seen.appendChild(icon("check", { size: 17 }));
    seen.addEventListener("click", async (ev) => {
      ev.stopPropagation();
      seen.disabled = true;
      const updated = await erledigeTicket(ticket.id);
      if (updated && nochOffen(updated)) {
        // Stapel: einer ist erledigt, der Rest bleibt stehen — und sagt es.
        seen.disabled = false;
        zeigeStapel(Number(updated.serieZahl ?? 0));
        meldeStapelStand(updated);
        window.dispatchEvent(new CustomEvent("voice-v8:tickets-changed"));
      } else if (updated) {
        // 2026-07-08 (Martin, 3x moniert): NUR diese Karte sanft entfernen —
        // KEIN Voll-Reload der Liste (gleiches Muster wie der Aufgaben-Tab).
        animateRowRemoval(row, 260);
        window.dispatchEvent(new CustomEvent("voice-v8:tickets-changed"));
      } else {
        seen.disabled = false;
        window.dispatchEvent(
          new CustomEvent("voice-v8:toast", {
            detail: {
              message: "Konnte nicht als gelesen markiert werden.",
              kind: "error",
            },
          }),
        );
      }
    });
    top.appendChild(seen);
  }
  if (actions.children.length > 0) main.appendChild(actions);

  row.appendChild(main);
  return row;
}

export function openTicketsModal(): void {
  // #328 (2026-07-14): Guard auf DOM-Wahrheit statt Stale-Ref — closeAllOverlays()
  // in app.ts entfernt den Backdrop DIREKT (ohne closeTicketsModal), die Ref
  // blieb gesetzt und die Glocke war bis zum Reload tot. isConnected heilt
  // ALLE fremden Schliesswege.
  if (openModalRef?.isConnected) return;
  openModalRef = null;
  /*
   * 08.09.2026 — auch „Offen" ueberlebt das Neuladen. Martin: *„wenn ich auf
   * einer Seite bin … und ich einen Reload mache, die Seite genau dort wieder
   * geladen wird."* Die Registrierung steht in app.ts bei den anderen sieben.
   */
  setPageHash("offen");

  // 2026-07-21 (Audit NORM v3, Martins Freigabe): unter der Topbar wie
  // alle Vollseiten, EIN Schliessweg (Pfeil), Escape schliesst.
  const backdrop = document.createElement("div");
  backdrop.className = "tickets-backdrop page-sheet";

  const modal = document.createElement("div");
  modal.className = "tickets-modal";
  backdrop.appendChild(modal);

  const onTicketsKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      closeTicketsModal();
      document.removeEventListener("keydown", onTicketsKey);
    }
  };
  document.addEventListener("keydown", onTicketsKey);
  let refresh: () => Promise<void> = async () => {};
  modal.appendChild(
    buildPageHeader("Offen", () => closeTicketsModal(), {
      menuItems: [
        { label: "Aktualisieren", onSelect: () => void refresh() },
      ],
    }),
  );

  /*
   * ⚠️ 07.09.2026 — „Offen" zeigt nur noch, was MARTIN gehoert.
   *
   * Martin, zum dritten Mal und diesmal unmissverstaendlich: *„Warum steht
   * das bitte in meinem Zuhause? Das hat mit mir ja nichts zu tun. Das ist
   * doch eine Sache, die du abarbeiten solltest."* Und: *„Der Reiter
   * E-Mails … eigentlich sollten wir den hier nicht mehr brauchen."*
   *
   * Am 03.09. wurde die Reihenfolge der Reiter geaendert — das war die
   * falsche Antwort auf dieselbe Beschwerde. Ein Entwickler-Ticket gehoert
   * nicht weiter nach hinten, es gehoert hier gar nicht hin. Die Regel dafuer
   * gibt es seit dem 29.08. und heisst `istZuhauseTun`; sie stand nur am
   * Zuhause und nicht an diesem Blatt.
   *
   * Damit bleibt EINE Liste: Meldungen, Faeden und Mails, das Wartende zuerst.
   * Reiter fuer eine einzige Liste waeren keine Wahl, sondern Zierde — sie
   * entfallen. Die Tickets sind nicht weg, sie sind nur nicht mehr seine
   * Sache (siehe /api/tickets und der Serien-Lauf).
   */
  const tabs = document.createElement("div");
  tabs.className = "tickets-modal-tabs offen-reiter";
  tabs.hidden = true;
  /*
   * ⚠️ 09.09.2026 — der Typ nannte noch „emails" und „tasks". Beide Reiter
   *    gibt es seit dem 03.09. nicht mehr, und die Rechnungen und E-Mails
   *    sind seit pre.2752 ganz aus Vespera heraus. Ein Typ, der Zustaende
   *    beschreibt, die es nicht gibt, laedt dazu ein, sie wieder zu bauen.
   */
  const filters: Array<{ key: "notes"; label: string }> = [
    /*
     * ⚠️ 2026-09-03 (Martin, zum zweiten Mal): „An erster Position sind die
     * Aufgaben, die in Wirklichkeit mit mir nichts zu tun haben — Sachen, die
     * das System abarbeitet. Das hatten wir schon mal."
     *
     * Er hat recht, und zwar woertlich: „Aufgaben" sind Tickets, die Vespera
     * fuer sich selbst anlegt (Fehler, Nacharbeiten). „Meldungen" sind SEINE
     * Sachen — Klimmzuege, Raus mit dir, die tiefe Frage.
     *
     * Ticket 1711 hatte die Aufgaben nach vorn geholt mit der Begruendung
     * „wer die Glocke oeffnet, sucht die Aufgaben". Das war aus der Sicht des
     * Systems gedacht, nicht aus seiner. Was ihn betrifft, steht vorn.
     */
    { key: "notes", label: "Meldungen" },
  ];
  /*
   * Es gibt nur noch eine Art; die Zuweisung unten setzt denselben Wert.
   * `as const` statt einer Typangabe — sonst beschreibt der Typ wieder mehr
   * Zustaende, als es gibt.
   */
  let activeFilter = "notes" as const;
  // Erledigt ist ein FILTER auf der aktuellen Art, kein eigener Tab
  // (Audit NORM v3: Filter = Chip, nie Tab).
  let showResolved = false;
  const tabButtons: HTMLButtonElement[] = [];
  for (const f of filters) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "tickets-modal-tab";
    b.dataset.active = f.key === activeFilter ? "true" : "false";
    b.dataset.key = f.key;
    b.textContent = f.label;
    b.addEventListener("click", () => {
      activeFilter = f.key;
      for (const btn of tabButtons) {
        // 2026-07-22: Vergleich ueber den Key — die Beschriftung traegt
        // jetzt eine Anzahl und ist nicht mehr vergleichsstabil.
        btn.dataset.active = btn.dataset.key === f.key ? "true" : "false";
      }
      void refresh();
    });
    tabs.appendChild(b);
    tabButtons.push(b);
  }
  const doneChip = document.createElement("button");
  doneChip.type = "button";
  doneChip.className = "tickets-done-chip";
  doneChip.textContent = "Erledigt";
  doneChip.dataset.active = "false";
  doneChip.addEventListener("click", () => {
    showResolved = !showResolved;
    doneChip.dataset.active = String(showResolved);
    void refresh();
  });
  modal.appendChild(tabs);
  // 2026-07-24 (Martin: "Erledigt raus, Segmente gleich gross"): der
  // Erledigt-Filter sitzt jetzt als ruhiger Schalter unter der Leiste.
  const filterbar = document.createElement("div");
  filterbar.className = "tickets-filterbar";

  /**
   * 2026-07-31 (Martin: "Bitte endlich saeubern, mich macht das wahnsinnig"):
   *
   * Als die Glocke von 654 automatischen Meldungen ueberflutet war, gab es
   * nur einen Weg heraus: jede Zeile einzeln antippen. Das ist keine
   * Aufraeumfunktion, das ist eine Strafe.
   *
   * Der Knopf erledigt GENAU DAS, WAS GERADE ZU SEHEN IST — die aktuelle
   * Art (Meldungen / E-Mails / Aufgaben) im offenen Blick. Nicht alles,
   * nicht die anderen Reiter. Deshalb steht die Anzahl in der Beschriftung:
   * Wer "Alle erledigen (37)" liest, weiss, dass 37 gemeint sind.
   *
   * Zwei Schritte statt eines Dialogs: Der erste Tipp spannt den Knopf, der
   * zweite loest aus. Ein Systemdialog waere hier falsch — er blockiert die
   * App und sieht nach Fehler aus, obwohl es eine gewollte Handlung ist.
   * Nach fuenf Sekunden ohne Bestaetigung entspannt er sich von selbst.
   *
   * Nichts geht verloren: Erledigtes steht weiter unter "Erledigt" und kann
   * dort einzeln zurueckgeholt werden.
   */
  let sichtbareZeilen: Ticket[] = [];
  let gespannt = false;
  let entspannTimer: number | null = null;
  let laeuftGerade = false;

  const sammelChip = document.createElement("button");
  sammelChip.type = "button";
  sammelChip.className = "tickets-bulk-chip";
  sammelChip.hidden = true;

  const entspannen = (): void => {
    gespannt = false;
    if (entspannTimer !== null) {
      window.clearTimeout(entspannTimer);
      entspannTimer = null;
    }
    beschriften();
  };

  const beschriften = (): void => {
    const n = sichtbareZeilen.length;
    // Nur im offenen Blick und nur, wenn es ueberhaupt etwas zu erledigen gibt.
    sammelChip.hidden = showResolved || n === 0 || laeuftGerade;
    sammelChip.dataset.armed = String(gespannt);
    sammelChip.textContent = gespannt
      ? `Wirklich? ${n} erledigen`
      : `Alle erledigen (${n})`;
  };

  const alleErledigen = async (): Promise<void> => {
    const liste = sichtbareZeilen.slice();
    if (liste.length === 0) return;
    laeuftGerade = true;
    entspannen();
    sammelChip.hidden = false;
    sammelChip.disabled = true;
    doneChip.disabled = true;

    let fertig = 0;
    let gescheitert = 0;
    const zeigeFortschritt = (): void => {
      sammelChip.textContent = `${fertig} von ${liste.length} …`;
    };
    zeigeFortschritt();

    // Begrenzte Gleichzeitigkeit: schnell genug, ohne das Telefon und den
    // Server mit hunderten offenen Verbindungen zugleich zu belegen.
    let naechster = 0;
    const arbeiter = async (): Promise<void> => {
      for (;;) {
        const i = naechster++;
        if (i >= liste.length) return;
        // „Alle erledigen" meint alle — auch ganze Stapel.
        const ok = await erledigeTicket(liste[i].id, true);
        if (ok) fertig++;
        else gescheitert++;
        zeigeFortschritt();
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(8, liste.length) }, () => arbeiter()),
    );

    laeuftGerade = false;
    sammelChip.disabled = false;
    doneChip.disabled = false;
    if (gescheitert > 0) {
      // Nicht still verschlucken: Was nicht ging, bleibt offen und sichtbar.
      sammelChip.textContent = `${gescheitert} nicht erledigt`;
      window.setTimeout(() => void refresh(), 2500);
      return;
    }
    await refresh();
  };

  sammelChip.addEventListener("click", () => {
    if (laeuftGerade) return;
    if (!gespannt) {
      gespannt = true;
      beschriften();
      entspannTimer = window.setTimeout(entspannen, 5000);
      return;
    }
    void alleErledigen();
  });

  filterbar.appendChild(sammelChip);
  filterbar.appendChild(doneChip);
  modal.appendChild(filterbar);

  // pre.871 (#145): Serien-Lauf-Steuerleiste (Start/Stopp + Status).
  const serialBar = document.createElement("div");
  serialBar.className = "tickets-serial-bar";
  serialBar.hidden = true;
  modal.appendChild(serialBar);

  const refreshSerialBar = async (): Promise<void> => {
    let st: {
      active?: boolean;
      currentTicketId?: number | null;
      queued?: number[];
    } = {};
    try {
      const r = await fetch("/api/serial-queue/status", {
        credentials: "include",
      });
      if (r.ok) st = await r.json();
    } catch {
      /* still */
    }
    const queued = st.queued ?? [];
    while (serialBar.firstChild) serialBar.removeChild(serialBar.firstChild);
    if (!st.active && queued.length === 0) {
      serialBar.hidden = true;
      return;
    }
    serialBar.hidden = false;
    const info = document.createElement("span");
    info.className = "tickets-serial-info";
    info.textContent = st.active
      ? `Serien-Lauf läuft${st.currentTicketId ? ` \u2014 gerade Ticket #${st.currentTicketId}` : ""} \u00b7 ${queued.length} in Warteschlange`
      : `${queued.length} Ticket(s) freigegeben \u2014 bereit für den Serien-Lauf`;
    serialBar.appendChild(info);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className =
      "tickets-btn " +
      (st.active ? "tickets-btn--ghost" : "tickets-btn--golive");
    btn.textContent = st.active ? "Serien-Lauf stoppen" : "Serien-Lauf starten";
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      try {
        const r = await fetch(
          st.active ? "/api/serial-queue/stop" : "/api/serial-queue/start",
          { method: "POST", credentials: "include" },
        );
        const j = (await r.json().catch(() => null)) as {
          ok?: boolean;
          message?: string;
        } | null;
        if (j?.message)
          window.dispatchEvent(
            new CustomEvent("voice-v8:toast", {
              detail: {
                message: j.message,
                kind: j.ok === false ? "warning" : "success",
              },
            }),
          );
      } catch {
        /* */
      }
      void refresh();
    });
    serialBar.appendChild(btn);
  };

  const body = document.createElement("div");
  body.className = "tickets-modal-body page-body";
  modal.appendChild(body);


  refresh = async (): Promise<void> => {
    // Der Serien-Lauf ist Entwickler-Sache und hat auf diesem Blatt nichts
    // mehr zu suchen — die Leiste bleibt zu.
    serialBar.hidden = true;
    body.textContent = "Lade…";
    const status = showResolved ? "resolved" : "open";
    const all = await fetchTickets(status);
    letzteOffeneTickets = showResolved ? [] : all;
    while (body.firstChild) body.removeChild(body.firstChild);
    /*
     * EINE Quelle, und sie fragt zuerst, wem die Zeile gehoert. Dieselbe
     * Regel wie am Zuhause (istZuhauseTun) — Entwickler-Tickets kommen hier
     * nicht mehr an, Meldungen, Faeden und Mails schon.
     */
    const meins = all.filter((t) => istZuhauseTun(t));
    const offen = teileOffene(meins);
    for (const btn of tabButtons) {
      btn.textContent =
        "Meldungen" + (offen.liste.length > 0 ? ` (${offen.liste.length})` : "");
    }
    // Faeden zuerst, dann Meldungen und Mails — teileOffene sortiert das.
    const rows: Ticket[] = offen.liste;
    // Der Sammel-Knopf arbeitet auf GENAU dieser Liste — dem, was zu sehen ist.
    sichtbareZeilen = rows;
    entspannen();
    if (rows.length === 0) {
      const empty = document.createElement("div");
      empty.className = "tickets-empty";
      empty.textContent = showResolved
        ? "Nichts Erledigtes."
        : "Nichts offen.";
      body.appendChild(empty);
      return;
    }
    for (const t of rows) {
      body.appendChild(
        istAufgabe(t.kind)
          ? renderTicketRow(t, () => void refresh())
          : renderNotificationRow(t, () => void refresh()),
      );
    }
  };

  document.body.appendChild(backdrop);
  openModalRef = backdrop;
  void refresh();
}

export function closeTicketsModal(): void {
  if (openModalRef) {
    openModalRef.remove();
    openModalRef = null;
  }
  clearPageHash("offen");
}

/**
 * Pre.445: Polling-Loop für den Glocken-Badge. Nur für Superuser
 * sichtbar — Server liefert für Non-Superuser count=0.
 */
/** Letzter angezeigter Stand — entscheidet ueber den Abfragetakt. */
let letzterBadgeStand = -1;

export function startTicketsBadgePolling(): void {
  if (badgeTaktLaeuft) return;
  badgeTaktLaeuft = true;
  const update = async (): Promise<boolean> => {
    try {
      const r = await fetch("/api/tickets?status=open", {
        credentials: "include",
      });
      if (!r.ok) return false;
      const j = (await r.json()) as {
        tickets?: Array<{
          title?: string;
          description?: string;
          kind?: string | null;
        }>;
      };
      const alle = Array.isArray(j.tickets) ? j.tickets : [];
      /*
       * Dieselbe Zahl wie das Blatt und das Zuhause — und seit dem 07.09.
       * heisst das: nur SEINE Zeilen. Vorher zaehlte die Glocke die
       * Entwickler-Tickets mit; sie stand auf 15, obwohl drei Sachen ihn
       * betrafen. Martin: „Warum sind immer noch 12 Aufgaben?"
       */
      const total = teileOffene(alle.filter((t) => istZuhauseTun(t))).liste
        .length;
      // 2026-07-31: Merken, ob sich die Zahl ueberhaupt geaendert hat — der
      // Takt richtet sich danach.
      const hatSichGeaendert = total !== letzterBadgeStand;
      letzterBadgeStand = total;
      // 2026-07-06 (Martin): dieselbe Zahl auch ans Home-Widget (Chat-Kachel
      // + kleines Widget). Der Badge-Poll ist die frischeste Quelle (laeuft
      // bei jeder Aktion, Fokus, Sichtbarkeit).
      void import("./native-recorder.js").then((m) =>
        m.pushAlltagWidgetInfo({ openTasks: total }),
      );
      // ⚠️ 15.09.2026: Hier stand die Glocke im Dock (Aufgaben-Knopf, seit
      //    dem 14.07.). Martin hat ihn aus der Fusszeile genommen — „Fuenf
      //    Elemente genuegen." Der Takt bleibt: er fuettert die Kachel im
      //    Home-Widget, die oben schon bedient wurde.
      return hatSichGeaendert;
    } catch {
      // Netzfehler wie "nichts geaendert" behandeln — der Takt streckt sich
      // dann, statt gegen ein totes Netz zu haemmern.
      return false;
    }
  };
  void update();
  // 2026-07-05 (Martin: "muss in Real-Time runterzaehlen"): aggressiv
  // aktualisieren — bei jeder Aktion, beim App-Zurueckkommen, bei Fokus und
  // haeufigerem Poll. So haengt das Badge nie auf einem alten Stand.
  window.addEventListener("voice-v8:tickets-changed", () => void update());
  window.addEventListener("focus", () => void update());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void update();
  });
  /**
   * 2026-07-31 (Martin: "mein Akku ist immer extrem schnell leer"):
   *
   * Hier stand ein starrer 15-Sekunden-Takt — gemessen 365 Anfragen pro
   * Stunde, fuer eine Zahl neben einem Symbol. Die aendert sich ein paar
   * Mal am Tag.
   *
   * Jetzt: schnell nach einer Aenderung, danach in Schritten langsamer bis
   * zwei Minuten. Kommt eine neue Meldung herein, faellt der Takt beim
   * naechsten Blick sofort wieder auf 15 Sekunden.
   */
  starteAnpassendenTakt({
    abfrage: update,
    schnellMs: 15_000,
    langsamMs: 120_000,
    name: "meldungs-zaehler",
  });
}
